import { useEffect, useState } from 'preact/hooks'
import { getLocalData, clearLocalData } from '../services/storage'
import { lockVisibleHeaders } from '../utils'
import SendIcon from './icons/PlaneIcon'
import Button from './ui/Button'
import LinkButton from './ui/LinkButton'
import AcceptPolicy from './AcceptPolicy'
import { useTranslation } from 'react-i18next'
import fomService from '../services/fomService'
import ResetIcon from './icons/ResetIcon'
import { trackSubmited } from '../services/trackService.js'
import debounce from '../services/debounce'
import { TargetFrame } from '../constants/index'
import useEmailControl from '../hooks/useEmailControl'
import CloseIcon from './icons/CloseIcon'
import Switch from './ui/Switch'
import SliderDashboard from './SliderDashboard'
import { useAppContext } from '../contexts/AppContext'
import { WebPlayQuestionFileUploadStorageService } from '../services/WebPlayQuestionFileUploadStorageService'
import Score from './Score'
import WebPlayActionCardService from '../services/WebPlayActionCardService'
import getNodesVisibilityRules from '../services/nodesVisibilityRules'
import WebPlayTitleGenerator from '../services/WebPlayTitleGenerator'
import { checkCompletionKeywords } from '../services/completionKeywordsService'
import { getActionCardModule } from '../actionCardModule'
const { FileQuestionNode } = await getActionCardModule()
import useActionCardData from '../hooks/useActionCardData'
import RedirectLinkReplacementProcessor from '../services/RedirectLinkReplacementProcessor.js'

const mailDebounce = debounce(300)

export default function Submit({
  setStatus,
  onOpenClose,
  isStorageEnabled,
  setIsStorageEnabled,
  isAcReady,
  setProgress,
}) {
  const { t } = useTranslation()
  const {
    playbookId,
    headersCount,
    redirectLink,
    targetFrame,
    submitPrompt,
    enableDocumentView,
    showScore,
    acRootRef,
    viewMode,
    loggedInUserId,
    playTitleTemplate,
    playTerm,
    completionKeywords,
  } = useAppContext()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isBrowOpened, setIsBrowOpened] = useState(false)
  const [brow, setBrow] = useState()
  const actionCardData = useActionCardData()

  const isFunnelView = viewMode === 'funnel'

  const {
    webPlayEmail,
    setWebPlayEmail,
    isPolicyAccepted,
    setIsPolicyAccepted,
    isWebPlayEmailInputVisible,
    isWebPlayEmailValid,
  } = useEmailControl()
  const isSendForbidden = isSubmitting || !isPolicyAccepted || !isWebPlayEmailValid

  const handleClick = async () => {
    if (!isFunnelView) {
      const slides = acRootRef.current.getSlidesData?.() || {}

      const allUnfilledRequiredIds = Object.values(slides).flatMap(value => {
        if (!value.isFilled) {
          return value.requiredDataIds
        }
        return []
      })

      if (allUnfilledRequiredIds?.length) {
        handleBrowClick()
        acRootRef.current.highlightElements(allUnfilledRequiredIds)

        return
      }
    }

    if (isSendForbidden) {
      return
    }

    let createdPlayId
    let createdPlayToken
    const actionCard = await getLocalData(playbookId)

    try {
      setIsSubmitting(true)

      const actionCardModel = new WebPlayActionCardService(actionCard).getActionCardModel()
      actionCardModel.nodesVisibilityRules.deleteRange(getNodesVisibilityRules())
      actionCardModel.adjustActionCardState()

      const keywords = actionCardModel.getActiveTagNames()

      if (!checkCompletionKeywords(keywords, completionKeywords)) {
        return
      }

      const lastActiveSeparator = actionCard.sections.find(s => s.id === actionCard.lastActiveSeparator)?.name
      const score = actionCard.score || 0
      lockVisibleHeaders(actionCard, headersCount)

      const title = new WebPlayTitleGenerator(playTerm, webPlayEmail, actionCardModel).generateTitle(playTitleTemplate)

      trackSubmited(playbookId, keywords)

      const { playId, fomToken } = await fomService.createPlay(
        playbookId,
        title,
        keywords,
        score,
        actionCard.scoringDimensions,
        lastActiveSeparator,
        actionCard.actionCardFlow,
        loggedInUserId,
      )

      createdPlayId = playId
      createdPlayToken = fomToken

      console.log('Created Play:', playId)

      actionCard.isFlowHidden = false

      // currently FileUpload-questions of action card contains data connected with local storage: it is necessary to remove them to not to copy them to the Play
      const fileNodesSearchService = actionCardModel.getSearchService().filter(x => x instanceof FileQuestionNode)
      fileNodesSearchService.forEach(x => (x.data.value = []))
      await uploadFiles(playId, fileNodesSearchService, fomToken)
      console.log('Files for file questions have been attached')

      await fomService.attachActionCardToPlay(playbookId, playId, actionCard, fomToken)
      console.log('Action card file has been attached')

      await fomService.sendPlayCreatedNotification(
        playbookId,
        playId,
        webPlayEmail,
        keywords,
        actionCardModel,
        fomToken,
      )

      console.log('Notification has been sent')

      try {
        if (redirectLink) {
          let targetRedirectLink = new RedirectLinkReplacementProcessor(
            actionCardModel,
            playTerm,
            webPlayEmail,
          ).process(redirectLink)
          targetRedirectLink = completeRedirectLinkWithCustomerParameter(actionCardModel, targetRedirectLink)

          switch (targetFrame) {
            case TargetFrame.SameFrame:
              location = targetRedirectLink
              return
            case TargetFrame.ParentFrame:
              window.parent.location = targetRedirectLink
              return
            case TargetFrame.NewTab:
              window.parent.open(targetRedirectLink)
          }
        }

        setIsSubmitted(true)
        setStatus({
          text: t('submit.success', 'Your data has been submitted successfully'),
          type: 'success',
        })
      } catch (error) {
        console.error(error)
      }
    } catch (e) {
      if (createdPlayId) {
        fomService.rollbackPlayCreation(playbookId, createdPlayId, createdPlayToken)
      }
      console.error(e)
      setStatus({
        text: t('submit.error', 'Failed to submit your data. Please try again'),
        type: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRestart = async () => {
    await clearLocalData(playbookId)
    window.location.href = window.location.href
  }

  const handleBrowClick = () => {
    setIsBrowOpened(!isBrowOpened)
    onOpenClose(!isBrowOpened)
  }

  useEffect(() => {
    const close = e => {
      if (e.key === 'Escape' && isBrowOpened) {
        setIsBrowOpened(false)
        onOpenClose(false)
      }
    }
    window.addEventListener('keydown', close)
    return () => {
      window.removeEventListener('keydown', close)
    }
  }, [isBrowOpened])

  const uploadFiles = async (projectId, fileNodesSearchService, fomToken) => {
    if (!playbookId) {
      return
    }
    const uploadPromises = []
    const filesData = await new WebPlayQuestionFileUploadStorageService(playbookId).getAll()
    filesData.forEach(fileData => {
      uploadPromises.push(uploadFile(fileNodesSearchService, projectId, fileData, fomToken))
    })

    const uploadedFiles = await Promise.all(uploadPromises)
    if (uploadedFiles.some(x => !!x.attachmentError)) {
      throw new Error('Some files have not been attached to a play')
    }
  }

  function uploadFile(fileNodesSearchService, projectId, fileData, fomToken) {
    return fomService
      .attachFileToPlay(playbookId, projectId, fileData.header.id, fileData.header.name, fileData.file, fomToken)
      .then(([id, url]) => {
        const fileNode = fileNodesSearchService.findById(fileData.questionId)
        fileNode?.data.value.push({ id, name: fileData.file.name, url })
        return { id: fileData.id, name: fileData.file.name, attachmentId: id, attachmentUrl: url }
      })
      .catch(error => {
        console.error(error)
        return { id: fileData.id, name: fileData.file.name, attachmentError: error.message }
      })
  }

  return (
    <div>
      {isBrowOpened ? <div onClick={handleBrowClick} className={'fixed inset-0 cursor-pointer'}></div> : null}
      <div
        ref={setBrow}
        style={{
          bottom: isBrowOpened ? '28px' : ``,
        }}
        className={`
          absolute left-0 right-0 flex flex-col gap-2 py-8 bg-white transition-[bottom] z-[5] border-t-[1px]
          dark:border-t-transparent
           ${
             enableDocumentView ? 'dark:bg-black border-t-[1px] dark:border-zinc-500' : 'dark:bg-wp-dark'
           } dark:text-white
        `}
      >
        <div className={`px-7 border-b-[1px] border-wp-border-gray dark:border-zinc-500 pb-4`}>
          {enableDocumentView && showScore && actionCardData && (
            <div className={'flex items-center justify-end font-bold py-2'}>
              <div className={'mr-2'}>{t('submit.totalScoreLabel', 'Total score')}:</div>
              <Score value={actionCardData.score} className={'border-[2px]'} />
            </div>
          )}

          {!isFunnelView && <div className="relative text-sm mb-5 max-h-48 overflow-auto">{submitPrompt}</div>}

          {isAcReady && (
            <SliderDashboard
              setIsBrowOpened={onOpenClose}
              isBrowOpened={isBrowOpened}
              handleBrowClick={handleBrowClick}
              setProgress={setProgress}
            />
          )}

          <button
            onClick={handleBrowClick}
            className={`
              group
              absolute right-0 top-0 flex items-center justify-center p-2 text-wp-gray hover:text-wp-dark  
            `}
          >
            <div className="w-4 h-4 rounded-full dark:group-hover:bg-white dark:group-hover:text-black">
              <CloseIcon />
            </div>
          </button>

          <Switch
            id="wp-remember-data"
            checked={isStorageEnabled}
            text={t('submit.saveInfoLabel', 'Remember my information')}
            onChange={checked => {
              setIsStorageEnabled(checked)
            }}
          />
        </div>

        <div className={`px-7`}>
          <EmailInput
            webPlayEmail={webPlayEmail}
            setWebPlayEmail={setWebPlayEmail}
            isVisible={isWebPlayEmailInputVisible}
          />
          <div className={'mb-4'}>
            <AcceptPolicy isPolicyAccepted={isPolicyAccepted} setIsPolicyAccepted={setIsPolicyAccepted} />
          </div>
          <div
            className="flex gap-2 items-center justify-center max-[410px]:flex-col max-[410px]:gap-0"
            title={!isPolicyAccepted ? t('submit.tooltip', 'Please accept the terms') : ''}
          >
            {!isSubmitted && (
              <Button
                label={t('submit.send', 'Send')}
                disabled={isSendForbidden}
                loading={isSubmitting}
                icon={<SendIcon size="16" />}
                onClick={handleClick}
              ></Button>
            )}

            {isSubmitted && (
              <Button
                label={t('submit.resend', 'Resend')}
                disabled={isSendForbidden}
                loading={isSubmitting}
                icon={<SendIcon size="16" />}
                onClick={handleClick}
              ></Button>
            )}

            {isSubmitted && (
              <LinkButton
                label={t('submit.restart', 'Restart')}
                icon={<ResetIcon size="16" />}
                onClick={handleRestart}
              ></LinkButton>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const EmailInput = ({ webPlayEmail, setWebPlayEmail, isVisible }) => {
  const { t } = useTranslation()

  const handleChange = e => {
    mailDebounce(() => setWebPlayEmail(e.target.value))
  }

  return (
    <div
      className={`
          flex justify-center overflow-hidden transition-all ${isVisible ? 'h-[75px]' : 'h-0'}
        `}
    >
      <div className={`flex flex-col pt-1 w-full`}>
        <label htmlFor="emailInput" className={'text-sm mb-1'}>
          E-Mail
        </label>
        <input
          onChange={handleChange}
          placeholder={t('submit.enterEmailLabel', 'Please enter your email')}
          id="emailInput"
          value={webPlayEmail ?? ''}
          className={`
            relative min-h-[38px] border-wp-border-gray border-solid dark:border-[#353535] 
           bg-white dark:bg-[#161616] border-[1px] rounded-[6px] dark:text-white px-[8px] w-full outline-none
          `}
          type="text"
        />
      </div>
    </div>
  )
}

function completeRedirectLinkWithCustomerParameter(actionCardModel, redirectLink) {
  /* it is temp code for one specific action card and it will be deleted soon (#14298) */
  const valueId = actionCardModel.getSearchService().findById(1181131805)?.getValue()?.id

  if (valueId) {
    const separator = redirectLink.includes('?') ? '&' : '?'

    switch (valueId) {
      case 3944468703:
        return `${redirectLink}${separator}audience=employee`
      case 2035047549:
        return `${redirectLink}${separator}audience=customer`
    }
  }

  return redirectLink
}
